/**
 * Traduz o erro de OAuth do GoTrue em orientação que a pessoa consegue seguir.
 *
 * A mensagem anterior era uma só, para qualquer falha: "entre com e-mail e
 * senha e avise o administrador". Conselho impossível — nenhuma conta deste
 * CRM tem senha (todas entram por Google, `encrypted_password` vazio). Quem
 * lia tentava o formulário de cima, falhava de novo e concluía que o sistema
 * estava fora do ar.
 *
 * O caso real que motivou isto (23/09/2026): uma pessoa com DUAS contas —
 * a corporativa, onde estavam os acessos, e uma pessoal criada depois. O
 * Google dela estava vinculado à segunda, e o login devolvia "User not
 * found". O que resolveria era conferir qual conta Google o navegador
 * escolheu, não tentar senha.
 */

export interface OAuthErrorHelp {
  /** O que o provedor disse, já legível. */
  detalhe: string;
  /** O que a pessoa pode fazer agora. Vazio quando não há ação útil. */
  acao: string;
}

/**
 * `code` é o `error_code` do hash (quando vem); `description` é o
 * `error_description`. A decisão usa o código quando existe, porque o texto
 * do provedor muda entre versões.
 */
export function oauthErrorHelp(description: string, code?: string | null): OAuthErrorHelp {
  const texto = `${code || ''} ${description}`.toLowerCase();

  // Conta Google sem usuário correspondente. Quase sempre é a pessoa tendo
  // mais de uma conta Google no navegador e o seletor escolhendo a errada.
  if (texto.includes('user not found') || texto.includes('user_not_found')) {
    return {
      detalhe: description,
      acao: 'Confira qual conta Google o navegador escolheu — se você tem mais de uma, '
        + 'saia de todas em accounts.google.com e entre de novo com a conta cadastrada aqui. '
        + 'Se continuar, peça ao administrador para verificar seu cadastro.',
    };
  }

  // Cadastro desativado: a pessoa não tem conta e não pode criar sozinha.
  if (texto.includes('signup') && (texto.includes('disabled') || texto.includes('not allowed'))) {
    return {
      detalhe: description,
      acao: 'Este CRM não cria conta sozinho. Peça um convite ao administrador.',
    };
  }

  // E-mail não verificado no provedor.
  if (texto.includes('email not confirmed') || texto.includes('unverified')) {
    return {
      detalhe: description,
      acao: 'Verifique o e-mail na sua conta Google e tente de novo.',
    };
  }

  // A pessoa fechou a janela do Google ou recusou a permissão.
  if (texto.includes('access_denied') || texto.includes('cancelled') || texto.includes('canceled')) {
    return {
      detalhe: 'Autorização cancelada.',
      acao: 'Clique em "Entrar com Google" de novo e conclua a permissão na janela do Google.',
    };
  }

  // Erro de configuração (client id/secret/redirect). A pessoa não resolve.
  if (texto.includes('redirect') || texto.includes('client') || texto.includes('invalid_request')) {
    return {
      detalhe: description,
      acao: 'Parece configuração do login, não algo do seu lado. Avise o administrador com esta mensagem.',
    };
  }

  return {
    detalhe: description,
    acao: 'Tente de novo. Se continuar, avise o administrador com esta mensagem.',
  };
}
